import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import type { MaterialNodeSpec } from '@/graph/domain/types';

interface StepHeaderProps {
  operationName: string;
  source: MaterialNodeSpec;
}

const MAX_VISIBLE_CONSTITUENTS = 3;

export function StepHeader({ operationName, source }: StepHeaderProps) {
  const constituents = source.constituents ?? [];
  const visibleNames = constituents
    .slice(0, MAX_VISIBLE_CONSTITUENTS)
    .map((c) => c.name);
  const hasMore = constituents.length > MAX_VISIBLE_CONSTITUENTS;
  const constituentDisplay = visibleNames.length
    ? `(${visibleNames.join(', ')}${hasMore ? ', ...' : ''})`
    : '';
  const fullConstituentList = constituents.map((c) => c.name).join(', ');

  const availableDisplay = source.symbolicQuantity
    ? String(source.symbolicQuantity)
    : source.quantity
      ? `${source.quantity.value.amount} ${source.quantity.value.units}`
      : 'unknown';

  return (
    <Box sx={{ mb: 2 }} data-testid="step-header">
      <Typography variant="subtitle2" color="text.secondary">
        Step Name
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
        {operationName}
      </Typography>

      <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
        Container
      </Typography>
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        {source.container?.name ?? '—'}
      </Typography>

      <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
        Material
      </Typography>
      <Tooltip title={fullConstituentList || 'No constituents'} arrow>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          {source.name} {constituentDisplay}
        </Typography>
      </Tooltip>

      <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
        Available
      </Typography>
      <Typography variant="body2">{availableDisplay}</Typography>
    </Box>
  );
}
